import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GenerateGuid } from './GuidChecks';
import { findHours } from './findHours';
import { IRateViewModel } from 'data/atoms/atomConfig';
import {
  ITicketDetailsInterface,
  ITicketPostBillingViewModel,
  ITicketPostViewModel,
} from 'Interfaces/ITicketConfig';

dayjs.extend(utc);

/**
 * Extended billing model including aggregated totals.
 */
export interface ITicketPostBillingWithTotals
  extends ITicketPostBillingViewModel {
  /** Sum of all cost fields */
  totalCost: number;
  /** Sum of all billing value fields */
  totalBillableValue: number;
}

// Default empty billing record
const ticketPostBillingEmpty: ITicketPostBillingViewModel = {
  id: 'new',
  ticketPostId: 'new',
  isBillable: false,
  isBilled: false,
  workCost: 0,
  workHours: 0,
  workBillableValue: 0,
  notBillableHours: 0,
  notBillableCost: 0,
  notBillableBillingValue: 0,
  eveningCost: 0,
  eveningHours: 0,
  eveningBillableValue: 0,
  midDayCost: 0,
  midDayHours: 0,
  midDayBillableValue: 0,
  nightCost: 0,
  nightHours: 0,
  nightBillableValue: 0,
};

/**
 * Round up hours to nearest quarter hour with 2 decimal precision.
 */
const roundUpToNearestQuarter = (hours: number): number => {
  const quarters = Math.ceil(hours * 4) / 4;
  return parseFloat(quarters.toFixed(2));
};

/**
 * Calculates detailed billing values from a ticket post.
 * Returns both the detailed breakdown and aggregated totals.
 */
export function calculateTicketPostBillingDetails(
  ticket: ITicketDetailsInterface,
  ticketPost: ITicketPostViewModel,
  rates: IRateViewModel[]
): ITicketPostBillingWithTotals {
  // Use existing billing or default
  const billingRecord: ITicketPostBillingViewModel =
    ticketPost.ticketPostBilling || ticketPostBillingEmpty;

  // Convert times from UTC
  const start = dayjs.utc(ticketPost.startTime).local();
  const end = dayjs.utc(ticketPost.endTime).local();

  // Compute total not-billable hours upfront
  const totalNotBillableHours = roundUpToNearestQuarter(
    findHours('notBillable', start, end, rates)
  );

  // Determine applicable rates and costs
  let hourlyRate = ticketPost.isInHouse
    ? ticketPost.workRole?.inHouseHourlyRate || 0
    : ticketPost.workRole?.onSiteHourlyRate || 0;
  const hourlyCost = ticketPost.isInHouse
    ? ticketPost.workRole?.inHouseHourlyRateCost || 0
    : ticketPost.workRole?.onSiteHourlyRateCost || 0;
  // oversturer hourlyrate og fixedhourlyrate
  hourlyRate =
    ticket.fixedHourlyRate > -1 ? ticket.fixedHourlyRate : hourlyRate;

  // Helper to apply multiplier
  const apply = (base: number, factor: number): number => base * factor;

  // Calculate for billable action
  if (ticketPost.isBillable) {
    billingRecord.isBillable = true;

    // Work hours
    const workHours = ticketPost.isOvertime
      ? roundUpToNearestQuarter(findHours('work', start, end, rates))
      : totalNotBillableHours;
    billingRecord.workHours = workHours;
    billingRecord.workCost = apply(hourlyCost * workHours, 1);
    billingRecord.workBillableValue = apply(hourlyRate * workHours, 1);

    // Midday hours
    const midDayHours = roundUpToNearestQuarter(
      findHours('midDay', start, end, rates)
    );
    billingRecord.midDayHours = midDayHours;
    billingRecord.midDayCost = apply(
      hourlyCost * midDayHours,
      ticketPost.isOvertime ? 1.5 : 1
    );
    billingRecord.midDayBillableValue = apply(
      hourlyRate * midDayHours,
      ticketPost.isOvertime ? 1.5 : 1
    );

    // Evening hours
    const eveningHours = roundUpToNearestQuarter(
      findHours('evening', start, end, rates)
    );
    billingRecord.eveningHours = eveningHours;
    billingRecord.eveningCost = apply(
      hourlyCost * eveningHours,
      ticketPost.isOvertime ? 1.5 : 1
    );
    billingRecord.eveningBillableValue = apply(
      hourlyRate * eveningHours,
      ticketPost.isOvertime ? 1.5 : 1
    );

    // Night hours
    const nightHours = roundUpToNearestQuarter(
      findHours('night', start, end, rates)
    );
    billingRecord.nightHours = nightHours;
    billingRecord.nightCost = apply(
      hourlyCost * nightHours,
      ticketPost.isOvertime ? 2 : 1
    );
    billingRecord.nightBillableValue = apply(
      hourlyRate * nightHours,
      ticketPost.isOvertime ? 2 : 1
    );

    // Zero out not-billable
    billingRecord.notBillableHours = 0;
    billingRecord.notBillableCost = 0;
    billingRecord.notBillableBillingValue = 0;
  } else {
    // Not billable
    billingRecord.isBillable = false;
    billingRecord.notBillableHours = totalNotBillableHours;
    billingRecord.notBillableCost = hourlyCost * totalNotBillableHours;
    billingRecord.notBillableBillingValue = hourlyRate * totalNotBillableHours;

    // Zero out billable categories
    billingRecord.workHours = 0;
    billingRecord.workCost = 0;
    billingRecord.workBillableValue = 0;
    billingRecord.midDayHours = 0;
    billingRecord.midDayCost = 0;
    billingRecord.midDayBillableValue = 0;
    billingRecord.eveningHours = 0;
    billingRecord.eveningCost = 0;
    billingRecord.eveningBillableValue = 0;
    billingRecord.nightHours = 0;
    billingRecord.nightCost = 0;
    billingRecord.nightBillableValue = 0;
  }

  // Ensure IDs
  if (!billingRecord.id || billingRecord.id === 'new') {
    billingRecord.id = GenerateGuid();
  }
  if (!billingRecord.ticketPostId || billingRecord.ticketPostId === 'new') {
    billingRecord.ticketPostId = ticketPost.id;
  }

  // Aggregate totals
  const totalCost =
    billingRecord.workCost +
    billingRecord.midDayCost +
    billingRecord.eveningCost +
    billingRecord.nightCost +
    billingRecord.notBillableCost;
  const totalBillableValue =
    billingRecord.workBillableValue +
    billingRecord.midDayBillableValue +
    billingRecord.eveningBillableValue +
    billingRecord.nightBillableValue +
    billingRecord.notBillableBillingValue;

  return { ...billingRecord, totalCost, totalBillableValue };
}
